import {
  Column,
  CreateDateColumn,
  Entity,
  Index,
  ManyToMany,
  ManyToOne,
  PrimaryGeneratedColumn,
  RelationId,
  DeleteDateColumn,
  UpdateDateColumn
} from 'typeorm'
import { Field, ID, ObjectType } from 'type-graphql'

import { Domain } from '@things-factory/shell'
import { Group } from '../group/group.js'
import { PlayGroup } from '../play-group/play-group.js'
import { User } from '@things-factory/auth-base'
import { config } from '@things-factory/env'

const ORMCONFIG = config.get('ormconfig', {})
const DATABASE_TYPE = ORMCONFIG.type

@Entity()
@Index('ix_board_1', (board: Board) => [board.domain, board.name], {
  unique: true,
  where: '"deleted_at" IS NULL'
})
@Index('ix_board_3', (board: Board) => [board.domain, board.group])
@ObjectType({ description: 'Represents a visual dashboard or display board.' })
export class Board {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { nullable: true, description: 'Unique identifier for the board.' })
  readonly id?: string

  @Column({ nullable: true, default: 1 })
  @Field({ nullable: true, description: 'The version of the board model.' })
  version?: number = 1

  @ManyToOne(type => Domain)
  @Field(type => Domain, { nullable: true, description: 'The domain to which this board belongs.' })
  domain?: Domain

  @RelationId((board: Board) => board.domain)
  domainId?: string

  @Column()
  @Field({ description: 'The name of the board.' })
  name?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'A detailed description of the board.' })
  description?: string

  @Column({ nullable: true, default: 'main' })
  @Field({ nullable: true, description: "The type of the board, can be 'main', 'sub', or 'popup'." })
  type?: 'main' | 'sub' | 'popup'

  @Column({ nullable: true, default: 'draft' })
  @Field({ nullable: true, description: "The state of the board, can be 'draft' or 'released'." })
  state?: 'draft' | 'released'

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'The JSON model defining the layout and components of the board.' })
  model?: string

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'A base64 encoded thumbnail image of the board.' })
  thumbnail?: string

  @ManyToOne(type => Group, group => group.boards)
  @Field(type => Group, { nullable: true, description: 'The group to which this board belongs.' })
  group?: Group

  @RelationId((board: Board) => board.group)
  groupId?: string

  @ManyToMany(type => PlayGroup, playGroup => playGroup.boards)
  @Field(type => [PlayGroup], { nullable: true, description: 'A list of play groups that this board is a part of.' })
  playGroups?: PlayGroup[]

  @CreateDateColumn()
  @Field({ nullable: true, description: 'The timestamp when the board was created.' })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true, description: 'The timestamp when the board was last updated.' })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'The user who created the board.' })
  creator?: User

  @RelationId((board: Board) => board.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'The user who last updated the board.' })
  updater?: User

  @RelationId((board: Board) => board.updater)
  updaterId?: string

  @DeleteDateColumn()
  @Field({ nullable: true, description: 'The timestamp when the board was soft-deleted.' })
  deletedAt?: Date
}
